import { BoardApiAction } from 'actions/BoardApiAction'
import { BoardConfig, PreviewType } from 'models/BoardConfig'
import { BoardItem } from 'models/BoardItem'
import { Me } from 'models/Me'
import { Resource } from 'models/Resource'
import { useCallback, useEffect, useReducer, useRef } from 'react'

const ACTION_GET = 'boardConfig/get' as const
const ACTION_GET_SUCCESS = 'boardConfig/get/success' as const
const ACTION_GET_FAILURE = 'boardConfig/get/failure' as const
const ACTION_ADD = 'boardConfig/add' as const
const ACTION_REMOVE = 'boardConfig/remove' as const
const ACTION_UPDATE = 'boardConfig/update' as const
const ACTION_SORT_TO_TOP = 'boardConfig/sortToTop' as const
const ACTION_SORT_TO_TOP_SUCCESS = 'boardConfig/sortToTop/success' as const
const ACTION_SORT_TO_TOP_FAILURE = 'boardConfig/sortToTop/failure' as const
const ACTION_LOAD_MORE = 'boardConfig/loadMore' as const
const ACTION_LOAD_MORE_SUCCESS = 'boardConfig/loadMore/success' as const
const ACTION_LOAD_MORE_FAILURE = 'boardConfig/loadMore/failure' as const
const ACTION_SWITCH_PREVIE_TYPE = 'boardConfig/switchPreviewType' as const

type BoardConfigState = {
    me?: Me
    resource: Resource<BoardConfig>
    isLoadingMore: boolean
    sorted?: BoardItem
}

const initialState: BoardConfigState = {
    resource: Resource.loading(),
    isLoadingMore: false,
}

const get = (me: Me) => {
    return { type: ACTION_GET, me }
}

const getSuccess = (board: BoardItem[], me: Me) => {
    return { type: ACTION_GET_SUCCESS, board, me }
}

const getFailure = (error: unknown) => {
    return { type: ACTION_GET_FAILURE, error }
}

const add = (item: BoardItem) => {
    return { type: ACTION_ADD, item }
}

const remove = (item: BoardItem) => {
    return { type: ACTION_REMOVE, item }
}

const update = (item: BoardItem) => {
    return { type: ACTION_UPDATE, item }
}

const sortToTop = (item: BoardItem) => {
    return { type: ACTION_SORT_TO_TOP, item }
}

const sortToTopSuccess = (item: BoardItem) => {
    return { type: ACTION_SORT_TO_TOP_SUCCESS, item }
}

const sortToTopFailure = (error: unknown) => {
    return { type: ACTION_SORT_TO_TOP_FAILURE, error }
}

const loadMore = () => {
    return { type: ACTION_LOAD_MORE }
}

const loadMoreSuccess = (items: BoardItem[]) => {
    return { type: ACTION_LOAD_MORE_SUCCESS, items }
}

const loadMoreFailure = (error: unknown) => {
    return { type: ACTION_LOAD_MORE_FAILURE, error }
}

const switchPreviewType = (previewType: PreviewType) => {
    return { type: ACTION_SWITCH_PREVIE_TYPE, previewType }
}

type Action = ReturnType<typeof get> 
    | ReturnType<typeof getSuccess>
    | ReturnType<typeof getFailure>
    | ReturnType<typeof add>
    | ReturnType<typeof remove>
    | ReturnType<typeof update>
    | ReturnType<typeof sortToTop>
    | ReturnType<typeof sortToTopSuccess>
    | ReturnType<typeof sortToTopFailure>
    | ReturnType<typeof loadMore>
    | ReturnType<typeof loadMoreSuccess>
    | ReturnType<typeof loadMoreFailure>
    | ReturnType<typeof switchPreviewType>

const reducer: React.Reducer<BoardConfigState, Action> = (state, action) => {
    switch(action.type) {
        case ACTION_GET:
            return {
                ...state,
                me: action.me,
                resource: Resource.loading(),
            }
        case ACTION_GET_SUCCESS:
            return {
                ...state,
                resource: Resource.of(BoardConfig.init(action.board, action.me.subsChannelProducts)),
            }
        case ACTION_GET_FAILURE:
            return {
                ...state,
                resource: Resource.failure(action.error),
            }
        case ACTION_ADD:
            return {
                ...state,
                resource: state.resource.transform((board) => board.added(action.item)),
            }
        case ACTION_REMOVE:
            return {
                ...state,
                resource: state.resource.transform((board) => board.removed(action.item)),
            }
        case ACTION_UPDATE:
            return {
                ...state,
                resource: state.resource.transform((board) => board.updated(action.item)),
            }
        case ACTION_SORT_TO_TOP:
            return state
        case ACTION_SORT_TO_TOP_SUCCESS:
            return {
                ...state,
                resource: state.resource.transform((board) => board.sortedToTop(action.item)),
                sorted: action.item,
            }
        case ACTION_SORT_TO_TOP_FAILURE:
            return state
        case ACTION_LOAD_MORE:
            return state
        case ACTION_LOAD_MORE_SUCCESS:
            return {
                ...state,
                resource: state.resource.transform((board) => board.loaded(action.items)),
                isLoadingMore: false,                
            }
        case ACTION_LOAD_MORE_FAILURE:
            return {
                ...state,
                isLoadingMore: false,
            }
        case ACTION_SWITCH_PREVIE_TYPE:
            return {
                ...state,
                previewType: action.previewType,
                resource: state.resource.transform((board) => board.switchPreviewType(action.previewType)),
            }
    }
}

type BoardConfitDispatcher = React.Dispatch<Action>

const sideEffect = async (state: BoardConfigState, action: Action, dispatcher: BoardConfitDispatcher) => {
    switch(action.type) {
        case ACTION_GET: {
            try {
                const res = await BoardApiAction.getMine(action.me.subsChannelProducts)
                dispatcher(getSuccess(res, action.me))
            } catch (e: unknown) {
                dispatcher(getFailure(e))
            }
            break;
        }
        case ACTION_SORT_TO_TOP: {
            try {
                await BoardApiAction.sortToTop(action.item.id)
                dispatcher(sortToTopSuccess(action.item))
            } catch (e: unknown) {
                dispatcher(sortToTopFailure(e))
            }
            break;
        }
        case ACTION_LOAD_MORE: {
            try {
                if (!state.me) {
                    throw Error('require me')
                }
                const nextOffset = state.resource.valueOrNull?.nextOffset
                if (nextOffset !== null && !state.isLoadingMore) {
                    const res = await BoardApiAction.getMine(state.me.subsChannelProducts, nextOffset)
                    dispatcher(loadMoreSuccess(res))
                }
            } catch (e: unknown) {
                dispatcher(loadMoreFailure(e))
            }
            break;
        }
    }
}

export type BoardConfigResult = {
    state: Resource<BoardConfig>
    add: (item: BoardItem) => void
    remove: (item: BoardItem) => void
    update: (item: BoardItem) => void
    sortToTop: (item: BoardItem) => void
    loadMore: () => void
    switchPreviewType: (type: PreviewType) => void
}

export const useBoardConfig: (me: Me | null, onSorted: () => void) => BoardConfigResult = (me, onSorted) => {
    const [state, dispatcher] = useReducer(reducer, initialState)
    const stateRef = useRef(initialState)
    stateRef.current = state
    const combined = useCallback((action: Action) => {
        sideEffect(stateRef.current, action, dispatcher)
        dispatcher(action)
    }, [dispatcher])

    const didCalled = useRef(false);
    
    useEffect(() => {
        if (me !== null && didCalled.current === false) {
            didCalled.current = true
            combined(get(me))
        }
    }, [me]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (state.sorted) {
            onSorted()
        }
    }, [state.sorted]) // eslint-disable-line react-hooks/exhaustive-deps

    const _add = useCallback((item: BoardItem) =>
        combined(add(item))
    , [combined])

    const _remove = useCallback((item: BoardItem) => 
        combined(remove(item))
    , [combined])

    const _update = useCallback((item: BoardItem) => 
        combined(update(item))
    , [combined])

    const _sortToTop = useCallback((item: BoardItem) => 
        combined(sortToTop(item))
    , [combined])

    const _loadMore = useCallback(() => {
        combined(loadMore())
    }, [combined, state.resource.valueOrNull?.nextOffset]) // eslint-disable-line react-hooks/exhaustive-deps

    const _switchPreviewType = useCallback((type: PreviewType) => {
        combined(switchPreviewType(type))
    }, [combined])

    return {
        state: state.resource,
        add: _add,
        remove: _remove,
        update: _update,
        sortToTop: _sortToTop,
        loadMore: _loadMore,
        switchPreviewType: _switchPreviewType,
    }
}
